import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Po usunięciu pozycji te spod niej dojeżdżają na jej miejsce, zamiast przeskakiwać.
 *
 * Wyłącznie dla list JEDNOKOLUMNOWYCH o pozycjach równej wysokości — droga to wysokość
 * jednej pozycji. W siatce wielokolumnowej pozycje wchodzą na zwolnione miejsce po skosie
 * i zmierzone tu przesunięcie byłoby nieprawdziwe w obu osiach.
 *
 * Ruch robi `transform`, a nie zwijanie wysokości: zmierzone w tej aplikacji — `height`
 * na `<tr>` nie rusza wiersza wcale (57 px przed, w trakcie i po), `translateY` przesuwa
 * go co do piksela.
 */
@Directive({
  selector: '[appSlideUpOnRemove]'
})
export class SlideUpOnRemoveDirective implements OnInit, OnDestroy {

  private static readonly DURATION_MS = 200;

  private observer: MutationObserver = null;

  constructor(private host: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    if (typeof MutationObserver === 'undefined' || this.prefersLessMotion()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(mutations => mutations.forEach(mutation => this.slideUpAfter(mutation)));
      this.observer.observe(this.host.nativeElement, { childList: true });
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private slideUpAfter(mutation: MutationRecord): void {
    const removed = this.elementCount(mutation.removedNodes);
    const first = removed && !mutation.addedNodes.length
      ? this.firstElementFrom(mutation.nextSibling)
      : null;

    // Usunięty węzeł jest już odpięty i ma zerowy rozmiar, więc drogę bierzemy z tego,
    // co weszło na jego miejsce.
    const distance = first ? first.offsetHeight * removed : 0;

    if (!distance) {
      return;
    }

    const following: HTMLElement[] = [];
    for (let element = first; element; element = element.nextElementSibling as HTMLElement) {
      following.push(element);
    }

    following.forEach(element => {
      element.style.transition = 'none';
      element.style.transform = `translateY(${distance}px)`;
    });

    // Wymuszony układ rozdziela oba zapisy `transform` na dwie klatki — bez tego
    // przeglądarka widzi samą wartość końcową i nie ma czego animować.
    void this.host.nativeElement.offsetHeight;

    following.forEach(element => {
      element.style.transition = `transform ${SlideUpOnRemoveDirective.DURATION_MS}ms ease-out`;
      element.style.transform = '';
      element.addEventListener('transitionend', () => element.style.transition = '', { once: true });
    });
  }

  private elementCount(nodes: NodeList): number {
    let count = 0;
    for (let i = 0; i < nodes.length; i++) {
      if (nodes.item(i).nodeType === Node.ELEMENT_NODE) {
        count++;
      }
    }
    return count;
  }

  private firstElementFrom(node: Node): HTMLElement {
    for (let current = node; current; current = current.nextSibling) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        return current as HTMLElement;
      }
    }
    return null;
  }

  /** WCAG 2.3.3 — kto prosił o mniej ruchu, dostaje natychmiastowe przeskoczenie. */
  private prefersLessMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
